import React, { useState, useEffect, useCallback, useMemo } from "react";
import PageHeader from "../../components/common/PageHeader";
import Spinner from "../../components/common/Spinner";
import ErrorAlert from "../../components/common/ErrorAlert";
import EmptyState from "../../components/common/EmptyState";
import SearchInput from "../../components/common/SearchInput";
import { categoriesApi } from "../../services/api";
import { DEFAULT_CATEGORIES } from "@akfdev-com/shared/src/models";

type CategoryType = "income" | "expense" | "asset" | "liability" | "equity";

interface Category {
  id: string;
  ownerId: string;
  nominalCode: string;
  name: string;
  parentCode?: string;
  categoryType: CategoryType;
  statementSection: "profit-loss" | "balance-sheet";
  displayOrder: number;
  faCode?: string;
  isSystem: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt?: string;
}

const TYPE_BADGES: Record<
  CategoryType,
  { bg: string; text: string; label: string }
> = {
  income: { bg: "bg-emerald-100", text: "text-emerald-800", label: "Income" },
  expense: { bg: "bg-red-100", text: "text-red-800", label: "Expense" },
  asset: { bg: "bg-sky-100", text: "text-sky-800", label: "Asset" },
  liability: { bg: "bg-amber-100", text: "text-amber-800", label: "Liability" },
  equity: { bg: "bg-purple-100", text: "text-purple-800", label: "Equity" },
};

const CategoriesList: React.FC = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [showInactive, setShowInactive] = useState(false);
  const [filterStatement, setFilterStatement] = useState<
    "all" | "profit-loss" | "balance-sheet"
  >("all");
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(
    new Set(),
  );

  // Add form state
  const [showAddForm, setShowAddForm] = useState(false);
  const [newCode, setNewCode] = useState("");
  const [newName, setNewName] = useState("");
  const [newParentCode, setNewParentCode] = useState("");
  const [newCategoryType, setNewCategoryType] =
    useState<CategoryType>("expense");
  const [saving, setSaving] = useState(false);

  // Edit state
  const [editingCode, setEditingCode] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [editParentCode, setEditParentCode] = useState("");
  const [editActive, setEditActive] = useState(true);

  const fetchCategories = useCallback(async () => {
    try {
      setLoading(true);
      const data = await categoriesApi.getAll();
      const items: Category[] = Array.isArray(data) ? data : [];
      items.sort((a, b) => (a.displayOrder ?? 999) - (b.displayOrder ?? 999));
      setCategories(items);
    } catch (err) {
      console.error("Error fetching categories:", err);
      setError("Failed to load categories. Please try again later.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  // All available parent categories for dropdowns — merges API data with DEFAULT_CATEGORIES
  // so the group selector always shows all standard groups even before they are seeded.
  const parentCategories = useMemo(() => {
    const apiParents = categories.filter((c) => !c.parentCode);
    const apiCodes = new Set(apiParents.map((p) => p.nominalCode));
    const syntheticParents: Category[] = DEFAULT_CATEGORIES.filter(
      (d) => !d.parentCode && !apiCodes.has(d.nominalCode),
    ).map((def) => ({
      id: "",
      ownerId: "",
      nominalCode: def.nominalCode,
      name: def.name,
      parentCode: undefined,
      categoryType: def.categoryType as CategoryType,
      statementSection: def.statementSection as "profit-loss" | "balance-sheet",
      displayOrder: def.displayOrder,
      faCode: def.faCode,
      isSystem: true,
      isActive: true,
      createdAt: "",
    }));
    return [...apiParents, ...syntheticParents].sort(
      (a, b) => (a.displayOrder ?? 999) - (b.displayOrder ?? 999),
    );
  }, [categories]);

  const filtered = categories.filter((cat) => {
    if (!cat.isSystem && !showInactive && !cat.isActive) return false;
    if (filterStatement !== "all" && cat.statementSection !== filterStatement)
      return false;
    if (search) {
      const term = search.toLowerCase();
      return (
        cat.nominalCode.toLowerCase().includes(term) ||
        cat.name.toLowerCase().includes(term) ||
        (cat.parentCode?.toLowerCase().includes(term) ?? false)
      );
    }
    return true;
  });

  // Group categories — DEFAULT_CATEGORIES provides the guaranteed group scaffold
  const grouped = useMemo(() => {
    const groups = new Map<
      string,
      { parent: Category; children: Category[] }
    >();
    const orphans: Category[] = [];

    // Always scaffold from DEFAULT system parent categories so groups show even
    // before the backend has seeded them for this owner.
    const systemParentDefs = DEFAULT_CATEGORIES.filter((d) => !d.parentCode);
    for (const def of systemParentDefs) {
      if (filterStatement !== "all" && def.statementSection !== filterStatement)
        continue;
      const apiParent = categories.find(
        (c) => c.nominalCode === def.nominalCode,
      );
      const parent: Category = apiParent ?? {
        id: "",
        ownerId: "",
        nominalCode: def.nominalCode,
        name: def.name,
        parentCode: undefined,
        categoryType: def.categoryType as CategoryType,
        statementSection: def.statementSection as
          | "profit-loss"
          | "balance-sheet",
        displayOrder: def.displayOrder,
        faCode: def.faCode,
        isSystem: true,
        isActive: true,
        createdAt: "",
      };
      groups.set(def.nominalCode, { parent, children: [] });
    }

    // Add any non-system parent categories from filtered (user-created top-level groups)
    for (const cat of filtered) {
      if (!cat.parentCode && !groups.has(cat.nominalCode)) {
        groups.set(cat.nominalCode, { parent: cat, children: [] });
      }
    }

    // Add children to their parent groups
    for (const cat of filtered) {
      if (cat.parentCode) {
        const group = groups.get(cat.parentCode);
        if (group) {
          group.children.push(cat);
        } else {
          // Parent filtered out — create pseudo-group from full categories list
          const parent = categories.find(
            (c) => c.nominalCode === cat.parentCode,
          );
          if (parent) {
            if (!groups.has(parent.nominalCode)) {
              groups.set(parent.nominalCode, { parent, children: [] });
            }
            groups.get(parent.nominalCode)!.children.push(cat);
          } else {
            orphans.push(cat);
          }
        }
      }
    }

    const sortedGroups = Array.from(groups.values()).sort(
      (a, b) => (a.parent.displayOrder ?? 999) - (b.parent.displayOrder ?? 999),
    );
    return { groups: sortedGroups, orphans };
  }, [filtered, categories, filterStatement]);

  const toggleGroup = (code: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  };

  const handleAdd = async () => {
    if (!newCode.trim() || !newName.trim()) return;
    setSaving(true);
    try {
      const parentCat = parentCategories.find(
        (p) => p.nominalCode === newParentCode,
      );
      await categoriesApi.create({
        nominalCode: newCode.trim(),
        name: newName.trim(),
        parentCode: newParentCode || undefined,
        categoryType: parentCat?.categoryType ?? newCategoryType,
        statementSection:
          parentCat?.statementSection ??
          (newCategoryType === "income" || newCategoryType === "expense"
            ? "profit-loss"
            : "balance-sheet"),
        displayOrder: 999,
      });
      setNewCode("");
      setNewName("");
      setNewParentCode("");
      setNewCategoryType("expense");
      setShowAddForm(false);
      await fetchCategories();
    } catch (err) {
      console.error("Error creating category:", err);
      setError("Failed to create category.");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (cat: Category) => {
    setEditingCode(cat.nominalCode);
    setEditName(cat.name);
    setEditParentCode(cat.parentCode || "");
    setEditActive(cat.isActive);
  };

  const handleSaveEdit = async () => {
    if (!editingCode || !editName.trim()) return;
    setSaving(true);
    try {
      const trimmedParent = editParentCode.trim();
      const newParent = trimmedParent
        ? parentCategories.find((p) => p.nominalCode === trimmedParent)
        : undefined;
      await categoriesApi.update(editingCode, {
        name: editName.trim(),
        parentCode: trimmedParent || null,
        isActive: editActive,
        ...(newParent
          ? {
              categoryType: newParent.categoryType,
              statementSection: newParent.statementSection,
            }
          : {}),
      });
      setEditingCode(null);
      await fetchCategories();
    } catch (err) {
      console.error("Error updating category:", err);
      setError("Failed to update category.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (cat: Category) => {
    if (!window.confirm(`Delete category "${cat.nominalCode} - ${cat.name}"?`))
      return;
    try {
      await categoriesApi.delete(cat.nominalCode);
      await fetchCategories();
    } catch (err) {
      console.error("Error deleting category:", err);
      setError("Failed to delete category.");
    }
  };

  const renderTypeBadge = (type: CategoryType) => {
    const badge = TYPE_BADGES[type] || TYPE_BADGES.expense;
    return (
      <span
        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}
      >
        {badge.label}
      </span>
    );
  };

  const renderRow = (cat: Category, isChild: boolean) => {
    return (
      <tr
        key={cat.nominalCode}
        className={[
          !cat.isActive ? "bg-gray-50 opacity-60" : "",
          isChild ? "bg-gray-50/50" : "",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        {editingCode === cat.nominalCode ? (
          <>
            <td className="px-6 py-3 whitespace-nowrap text-sm font-mono font-medium text-gray-900">
              {isChild && <span className="text-gray-300 mr-1">└</span>}
              {cat.nominalCode}
            </td>
            <td className="px-6 py-3 whitespace-nowrap">
              <input
                type="text"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              />
            </td>
            <td className="px-6 py-3 whitespace-nowrap">
              {renderTypeBadge(cat.categoryType)}
            </td>
            <td className="px-6 py-3 whitespace-nowrap">
              <select
                value={editParentCode}
                onChange={(e) => setEditParentCode(e.target.value)}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              >
                <option value="">No parent</option>
                {parentCategories
                  .filter((p) => p.nominalCode !== cat.nominalCode)
                  .map((p) => (
                    <option key={p.nominalCode} value={p.nominalCode}>
                      {p.nominalCode} - {p.name}
                    </option>
                  ))}
              </select>
            </td>
            <td className="px-6 py-3 whitespace-nowrap">
              <label className="flex items-center text-sm">
                <input
                  type="checkbox"
                  checked={editActive}
                  onChange={(e) => setEditActive(e.target.checked)}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 mr-2"
                />
                Active
              </label>
            </td>
            <td className="px-6 py-3 whitespace-nowrap text-right text-sm space-x-2">
              <button
                onClick={handleSaveEdit}
                disabled={saving}
                className="text-green-600 hover:text-green-900"
              >
                Save
              </button>
              <button
                onClick={() => setEditingCode(null)}
                className="text-gray-500 hover:text-gray-700"
              >
                Cancel
              </button>
            </td>
          </>
        ) : (
          <>
            <td className="px-6 py-3 whitespace-nowrap text-sm font-mono font-medium text-gray-900">
              {isChild && <span className="text-gray-300 mr-1">└</span>}
              {cat.nominalCode}
            </td>
            <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-900">
              {cat.name}
            </td>
            <td className="px-6 py-3 whitespace-nowrap text-sm">
              {renderTypeBadge(cat.categoryType)}
            </td>
            <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-500">
              {cat.statementSection === "profit-loss" ? "P&L" : "Balance Sheet"}
            </td>
            <td className="px-6 py-3 whitespace-nowrap text-sm">
              <div className="flex items-center gap-2">
                <span
                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    cat.isActive
                      ? "bg-green-100 text-green-800"
                      : "bg-gray-100 text-gray-800"
                  }`}
                >
                  {cat.isActive ? "Active" : "Inactive"}
                </span>
                {cat.isSystem && (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
                    System
                  </span>
                )}
              </div>
            </td>
            <td className="px-6 py-3 whitespace-nowrap text-right text-sm space-x-2">
              <button
                onClick={() => handleEdit(cat)}
                className="text-primary-600 hover:text-blue-900"
              >
                Edit
              </button>
              {!cat.isSystem && (
                <button
                  onClick={() => handleDelete(cat)}
                  className="text-red-600 hover:text-red-900"
                >
                  Delete
                </button>
              )}
            </td>
          </>
        )}
      </tr>
    );
  };

  if (loading) return <Spinner message="Loading categories..." />;
  if (error) return <ErrorAlert message={error} />;

  return (
    <div className="">
      <PageHeader
        title="Chart of Accounts v1"
        description="Manage accounting categories and nominal codes"
        actions={
          <button
            onClick={() => setShowAddForm(!showAddForm)}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700"
          >
            {showAddForm ? "Cancel" : "Add Category"}
          </button>
        }
      />

      {/* Add form */}
      {showAddForm && (
        <div className="mt-4 bg-white shadow rounded-lg p-4">
          <h3 className="text-sm font-medium text-gray-700 mb-3">
            New Category
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-5 gap-3">
            <input
              type="text"
              placeholder="Nominal Code (e.g. 2008)"
              value={newCode}
              onChange={(e) => setNewCode(e.target.value)}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
            />
            <input
              type="text"
              placeholder="Name (e.g. Electricity)"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
            />
            <select
              value={newParentCode}
              onChange={(e) => {
                setNewParentCode(e.target.value);
                const parent = parentCategories.find(
                  (p) => p.nominalCode === e.target.value,
                );
                if (parent) setNewCategoryType(parent.categoryType);
              }}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
            >
              <option value="">No parent (top-level)</option>
              {parentCategories.map((p) => (
                <option key={p.nominalCode} value={p.nominalCode}>
                  {p.nominalCode} - {p.name}
                </option>
              ))}
            </select>
            {!newParentCode && (
              <select
                value={newCategoryType}
                onChange={(e) =>
                  setNewCategoryType(e.target.value as CategoryType)
                }
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              >
                <option value="income">Income</option>
                <option value="expense">Expense</option>
                <option value="asset">Asset</option>
                <option value="liability">Liability</option>
                <option value="equity">Equity</option>
              </select>
            )}
            <button
              onClick={handleAdd}
              disabled={saving || !newCode.trim() || !newName.trim()}
              className="inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 disabled:opacity-50"
            >
              {saving ? "Saving..." : "Save"}
            </button>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="mt-6 bg-white shadow rounded-lg p-4">
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
          <div className="flex-1">
            <SearchInput
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onClear={() => setSearch("")}
              placeholder="Search by code or name..."
            />
          </div>
          <select
            value={filterStatement}
            onChange={(e) =>
              setFilterStatement(
                e.target.value as "all" | "profit-loss" | "balance-sheet",
              )
            }
            className="rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
          >
            <option value="all">All Statements</option>
            <option value="profit-loss">Profit &amp; Loss</option>
            <option value="balance-sheet">Balance Sheet</option>
          </select>
          <label className="flex items-center text-sm text-gray-600">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 mr-2"
            />
            Show inactive
          </label>
        </div>
      </div>

      {/* Summary */}
      <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white overflow-hidden shadow rounded-lg px-4 py-5 sm:p-6">
          <dt className="text-sm font-medium text-gray-500 truncate">
            Total Categories
          </dt>
          <dd className="mt-1 text-2xl font-semibold text-gray-900">
            {categories.length}
          </dd>
        </div>
        <div className="bg-white overflow-hidden shadow rounded-lg px-4 py-5 sm:p-6">
          <dt className="text-sm font-medium text-gray-500 truncate">Active</dt>
          <dd className="mt-1 text-2xl font-semibold text-green-600">
            {categories.filter((c) => c.isActive).length}
          </dd>
        </div>
        <div className="bg-white overflow-hidden shadow rounded-lg px-4 py-5 sm:p-6">
          <dt className="text-sm font-medium text-gray-500 truncate">
            System Categories
          </dt>
          <dd className="mt-1 text-2xl font-semibold text-primary-600">
            {categories.filter((c) => c.isSystem).length}
          </dd>
        </div>
      </div>

      {/* Grouped Table */}
      {filtered.length === 0 ? (
        <div className="mt-6">
          <EmptyState
            title="No categories found"
            description={
              search
                ? "No categories match your search. Try a different term."
                : "Get started by adding your first accounting category."
            }
            actionText={!search ? "Add Category" : undefined}
            onAction={!search ? () => setShowAddForm(true) : undefined}
          />
        </div>
      ) : (
        <div className="mt-6 bg-white shadow overflow-hidden rounded-lg">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Code
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Statement
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {grouped.groups.map(({ parent, children }) => {
                  const isCollapsed = collapsedGroups.has(parent.nominalCode);
                  const isEditingParent = editingCode === parent.nominalCode;
                  return (
                    <React.Fragment key={parent.nominalCode}>
                      {isEditingParent ? (
                        <tr className="bg-blue-50">
                          <td className="px-6 py-3 whitespace-nowrap text-sm font-mono font-bold text-gray-900">
                            {parent.nominalCode}
                          </td>
                          <td className="px-6 py-3 whitespace-nowrap">
                            <input
                              type="text"
                              value={editName}
                              onChange={(e) => setEditName(e.target.value)}
                              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                            />
                          </td>
                          <td className="px-6 py-3 whitespace-nowrap text-sm">
                            {renderTypeBadge(parent.categoryType)}
                          </td>
                          <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-400 italic">
                            Top-level
                          </td>
                          <td className="px-6 py-3 whitespace-nowrap">
                            <label className="flex items-center text-sm">
                              <input
                                type="checkbox"
                                checked={editActive}
                                onChange={(e) =>
                                  setEditActive(e.target.checked)
                                }
                                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 mr-2"
                              />
                              Active
                            </label>
                          </td>
                          <td className="px-6 py-3 whitespace-nowrap text-right text-sm space-x-2">
                            <button
                              onClick={handleSaveEdit}
                              disabled={saving}
                              className="text-green-600 hover:text-green-900"
                            >
                              Save
                            </button>
                            <button
                              onClick={() => setEditingCode(null)}
                              className="text-gray-500 hover:text-gray-700"
                            >
                              Cancel
                            </button>
                          </td>
                        </tr>
                      ) : (
                        <tr
                          className="cursor-pointer transition-colors bg-gray-100 hover:bg-gray-200"
                          onClick={() => toggleGroup(parent.nominalCode)}
                        >
                          <td className="px-6 py-3 whitespace-nowrap text-sm font-mono font-bold text-gray-900">
                            <span className="mr-2 text-gray-400">
                              {isCollapsed ? "▸" : "▾"}
                            </span>
                            {parent.nominalCode}
                          </td>
                          <td className="px-6 py-3 whitespace-nowrap text-sm font-semibold text-gray-900">
                            {parent.name}
                            <span className="ml-2 text-xs text-gray-400">
                              ({children.length})
                            </span>
                          </td>
                          <td className="px-6 py-3 whitespace-nowrap text-sm">
                            {renderTypeBadge(parent.categoryType)}
                          </td>
                          <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-500">
                            {parent.statementSection === "profit-loss"
                              ? "P&L"
                              : "Balance Sheet"}
                          </td>
                          <td className="px-6 py-3 whitespace-nowrap text-sm">
                            <div className="flex items-center gap-2">
                              {parent.isSystem && (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
                                  System
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-3 whitespace-nowrap text-right text-sm space-x-2">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEdit(parent);
                              }}
                              className="text-primary-600 hover:text-blue-900"
                            >
                              Edit
                            </button>
                          </td>
                        </tr>
                      )}
                      {!isCollapsed &&
                        !isEditingParent &&
                        children.map((child) => renderRow(child, true))}
                    </React.Fragment>
                  );
                })}
                {grouped.orphans.map((cat) => renderRow(cat, false))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default CategoriesList;
